import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Building2, Printer } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { name: "", code: "", address: "", city: "", phone: "", opening_hours: "07:30 - 16:30", is_active: true, printer_mode: "browser", printer_host: "", printer_port: 9100 };

function printerLabel(agency) {
  if (agency.printer_mode === "network") {
    return `Imprimante réseau ${agency.printer_host || "?"}:${agency.printer_port || 9100}`;
  }
  if (agency.printer_mode === "webusb") return "WebUSB ESC/POS";
  return "Navigateur (PDF)";
}

export default function AgenciesPage() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const isSuper = user?.role === "SUPER_ADMIN";

  const refresh = () => api.get("/agencies").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const submit = async () => {
    try {
      if (editing) {
        await api.patch(`/agencies/${editing.id}`, form);
        toast.success("Agence mise à jour");
      } else {
        await api.post("/agencies", form);
        toast.success("Agence créée");
      }
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const onEdit = (a) => { setEditing(a); setForm({ ...EMPTY, ...a }); setOpen(true); };
  const onDelete = async (a) => {
    if (!confirm(`Supprimer l'agence ${a.name} ?`)) return;
    await api.delete(`/agencies/${a.id}`);
    toast.success("Agence supprimée");
    refresh();
  };

  return (
    <div className="space-y-6" data-testid="agencies-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-bold text-3xl text-slate-900">Agences</h1>
          <p className="text-slate-500 text-sm mt-1">Gérez le réseau d'agences MAMA.</p>
        </div>
        {isSuper && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(EMPTY); } }}>
            <DialogTrigger asChild>
              <Button className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="add-agency-button">
                <Plus className="w-4 h-4 mr-1" /> Nouvelle agence
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editing ? "Modifier" : "Nouvelle"} agence</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="agency-name-input" /></div>
                  <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="agency-code-input" /></div>
                </div>
                <div><Label>Adresse</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Ville</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                  <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                </div>
                <div><Label>Horaires d'ouverture</Label><Input value={form.opening_hours} onChange={(e) => setForm({ ...form, opening_hours: e.target.value })} /></div>
                <div className="border-t pt-4 space-y-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <Printer className="w-4 h-4" /> Imprimante de tickets
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label>Mode</Label>
                      <Select value={form.printer_mode || "browser"} onValueChange={(v) => setForm({ ...form, printer_mode: v })}>
                        <SelectTrigger data-testid="agency-printer-mode"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="browser">Navigateur (PDF)</SelectItem>
                          <SelectItem value="webusb">WebUSB (ESC/POS)</SelectItem>
                          <SelectItem value="network">Réseau IP (ESC/POS)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2 grid grid-cols-3 gap-2">
                      <div className="col-span-2"><Label>Adresse IP</Label>
                        <Input value={form.printer_host || ""} disabled={form.printer_mode !== "network"}
                          onChange={(e) => setForm({ ...form, printer_host: e.target.value })} placeholder="192.168.1.50" />
                      </div>
                      <div><Label>Port</Label>
                        <Input type="number" value={form.printer_port || 9100} disabled={form.printer_mode !== "network"}
                          onChange={(e) => setForm({ ...form, printer_port: parseInt(e.target.value || "9100") })} />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} /><Label>Active</Label></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button onClick={submit} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="agency-submit">{editing ? "Enregistrer" : "Créer"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((a) => (
          <Card key={a.id} className="p-5 border-slate-200 shadow-none">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-[#DC2626]/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-[#DC2626]" />
                </div>
                <div>
                  <div className="font-display font-bold text-lg text-slate-900">{a.name}</div>
                  <div className="text-xs text-slate-500 font-mono uppercase tracking-wider">{a.code} • {a.city}</div>
                </div>
              </div>
              {isSuper && (
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => onEdit(a)}><Pencil className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => onDelete(a)}><Trash2 className="w-4 h-4 text-[#DC2626]" /></Button>
                </div>
              )}
            </div>
            <div className="mt-4 space-y-1 text-sm text-slate-600">
              <div>{a.address}</div>
              <div>{a.phone}</div>
              <div className="text-xs text-slate-500">Horaires: {a.opening_hours}</div>
              <div className="text-xs flex items-center gap-1 text-slate-500 pt-1">
                <Printer className="w-3 h-3" />
                {printerLabel(a)}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
